import defaultWinShortcuts from "../configs/default_win_shortcuts.js";
import defaultMacShortcuts from "../configs/default_mac_shortcuts.js";

export const KEYBOARD_SHORTCUTS_STORAGE_KEY = "cc-shortcuts";
export const KEYBOARD_SHORTCUTS_UPDATED_EVENT = "cc-shortcuts-updated";

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function parseShortcutGroups(shortcuts) {
    if (typeof shortcuts === "string") {
        return JSON.parse(shortcuts);
    }

    return shortcuts;
}

function validateShortcutGroups(shortcuts) {
    if (!Array.isArray(shortcuts)) {
        throw new Error("Keyboard shortcuts must be an array of groups.");
    }

    shortcuts.forEach((group, groupIndex) => {
        if (!group || typeof group !== "object" || Array.isArray(group)) {
            throw new Error(`Shortcut group at index ${groupIndex} must be an object.`);
        }

        if (typeof group.name !== "string" || !group.name) {
            throw new Error(`Shortcut group at index ${groupIndex} must include a name.`);
        }

        if (!Array.isArray(group.shortcuts)) {
            throw new Error(`Shortcut group "${group.name}" must include a shortcuts array.`);
        }

        group.shortcuts.forEach((shortcut, shortcutIndex) => {
            if (!shortcut || typeof shortcut !== "object" || Array.isArray(shortcut)) {
                throw new Error(
                    `Shortcut at index ${shortcutIndex} in group "${group.name}" must be an object.`,
                );
            }

            if (typeof shortcut.name !== "string" || !shortcut.name) {
                throw new Error(
                    `Shortcut at index ${shortcutIndex} in group "${group.name}" must include a name.`,
                );
            }

            if (typeof shortcut.keyCmd !== "string") {
                throw new Error(`Shortcut "${shortcut.name}" must include a keyCmd string.`);
            }
        });
    });

    return shortcuts;
}

export function normalizeKeyboardShortcuts(shortcuts) {
    return validateShortcutGroups(parseShortcutGroups(shortcuts));
}

export function getDefaultKeyboardShortcuts(isMac) {
    return clone(isMac ? defaultMacShortcuts : defaultWinShortcuts);
}

export function serializeKeyboardShortcuts(shortcutGroups) {
    return shortcutGroups.map((group) => {
        return {
            ...Object.fromEntries(
                Object.entries(group).filter(([, value]) => {
                    return typeof value !== "function" && value !== undefined;
                }),
            ),
            shortcuts: group.shortcuts.map((shortcut) => {
                return Object.fromEntries(
                    Object.entries(shortcut).filter(([, value]) => {
                        return typeof value !== "function" && value !== undefined;
                    }),
                );
            }),
        };
    });
}

export function getStoredKeyboardShortcuts(isMac) {
    const defaults = getDefaultKeyboardShortcuts(isMac);

    if (typeof localStorage === "undefined") {
        return defaults;
    }

    let storedShortcuts;

    try {
        storedShortcuts = localStorage.getItem(KEYBOARD_SHORTCUTS_STORAGE_KEY);
    } catch (err) {
        console.warn("Failed to read keyboard shortcuts from localStorage:", err);
        return defaults;
    }

    if (!storedShortcuts) {
        return defaults;
    }

    try {
        const parsedStored = normalizeKeyboardShortcuts(storedShortcuts);
        const { shortcuts } = mergeKeyboardShortcuts(defaults, parsedStored);

        if (import.meta.env?.DEV) {
            console.debug(
                `Loaded keyboard shortcuts from localStorage (${storedShortcuts.length} bytes, merged with defaults)`,
            );
        }

        return shortcuts;
    } catch (err) {
        console.warn(
            "Failed to load keyboard shortcuts from localStorage; using defaults:",
            err,
        );
        return defaults;
    }
}

export function mergeKeyboardShortcuts(baseShortcuts, shortcutUpdates) {
    let mergedShortcuts = serializeKeyboardShortcuts(baseShortcuts);
    let updates = normalizeKeyboardShortcuts(shortcutUpdates);
    let updatedCount = 0;
    let ignoredCount = 0;

    updates.forEach((group) => {
        let matchingGroup = mergedShortcuts.find((shortcutGroup) => {
            return shortcutGroup.name === group.name;
        });

        if (!matchingGroup) {
            ignoredCount += group.shortcuts.length;
            return;
        }

        group.shortcuts.forEach((shortcutUpdate) => {
            let matchingShortcut = matchingGroup.shortcuts.find((shortcut) => {
                return shortcut.name === shortcutUpdate.name;
            });

            if (!matchingShortcut) {
                ignoredCount += 1;
                return;
            }

            if (shortcutUpdate.keyCmd) {
                let conflictingShortcut = findShortcutByKeyCmd(
                    shortcutUpdate.keyCmd,
                    mergedShortcuts,
                );

                if (
                    conflictingShortcut &&
                    conflictingShortcut !== matchingShortcut
                ) {
                    conflictingShortcut.keyCmd = "";
                }
            }

            matchingShortcut.keyCmd = shortcutUpdate.keyCmd;
            updatedCount += 1;
        });
    });

    return {
        shortcuts: mergedShortcuts,
        updatedCount,
        ignoredCount,
    };
}

function findShortcutByKeyCmd(keyCmd, shortcutGroups) {
    for (let group of shortcutGroups) {
        let shortcut = group.shortcuts.find((shortcutItem) => {
            return shortcutItem.keyCmd === keyCmd;
        });

        if (shortcut) {
            return shortcut;
        }
    }

    return null;
}

export function saveKeyboardShortcuts(shortcuts) {
    let normalizedShortcuts;

    try {
        normalizedShortcuts = serializeKeyboardShortcuts(
            normalizeKeyboardShortcuts(shortcuts),
        );
    } catch (err) {
        console.error("Failed to normalize keyboard shortcuts for save:", err);
        return {
            success: false,
            shortcuts: [],
            error: err.message,
        };
    }

    if (typeof localStorage === "undefined") {
        console.error("localStorage is not available; keyboard shortcuts were not saved.");
        return {
            success: false,
            shortcuts: normalizedShortcuts,
            error: "localStorage is not available",
        };
    }

    try {
        localStorage.setItem(
            KEYBOARD_SHORTCUTS_STORAGE_KEY,
            JSON.stringify(normalizedShortcuts),
        );
    } catch (err) {
        console.error("Failed to save keyboard shortcuts to localStorage:", err);
        return {
            success: false,
            shortcuts: normalizedShortcuts,
            error: err.message,
        };
    }

    return {
        success: true,
        shortcuts: normalizedShortcuts,
    };
}

export function dispatchKeyboardShortcutsUpdated(shortcuts) {
    window.dispatchEvent(
        new CustomEvent(KEYBOARD_SHORTCUTS_UPDATED_EVENT, {
            detail: {
                shortcuts: serializeKeyboardShortcuts(shortcuts),
            },
        }),
    );
}
